import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { format } from "date-fns";
import { useAtom } from "jotai";

import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import {
  cadenceOptions,
  cadenceValueForRecurrence,
  getRecurrenceForCadence,
  getMaximumOccurrenceCount,
  getAvailableRecurrenceCadenceValues,
  recurrenceEndDate,
  nextOccurrenceDateAfter,
} from "@/features/RecurringRequests/recurrence";
import { DatePickerInput } from "@whoosh/design-system/src/components/controls/Datepickers";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import {
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls/RadioList";
import { ToggleSwitch } from "@whoosh/design-system/src/components/controls/ToggleSwitch";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

type RecurringRequestControlsProps = {
  clubWeekStartsOn: 0 | 1;
  maximumEndDate: string;
  sourceDate: string;
};

export const RecurringRequestControls = ({
  clubWeekStartsOn,
  maximumEndDate,
  sourceDate,
}: RecurringRequestControlsProps) => {
  const { i18n } = useLingui();
  const [bookingWizardData, setBookingWizardData] = useAtom(
    BookingWizardDataAtom
  );

  if (bookingWizardData?.type !== "RequestBooking") {
    return null;
  }

  const recurrence = bookingWizardData.recurrence ?? {
    enabled: false,
    recurrenceType: "WEEK",
    repeatInterval: 1,
    endCondition: "AFTER_OCCURRENCES",
    occurrenceCount: 4,
  };

  const cadenceValue = cadenceValueForRecurrence(recurrence);

  const maximumOccurrenceCount = getMaximumOccurrenceCount({
    maximumEndDate,
    recurrenceType: recurrence.recurrenceType,
    repeatInterval: recurrence.repeatInterval,
    sourceDate,
  });
  const availableCadenceValues = getAvailableRecurrenceCadenceValues({
    maximumEndDate,
    sourceDate,
  });
  const availableCadenceOptions = cadenceOptions
    .filter((option) => availableCadenceValues.includes(option.value))
    .map((option) => ({ ...option, label: i18n._(option.label) }));
  const selectedEndDate = recurrenceEndDate(sourceDate, recurrence);
  const firstFutureOccurrenceDate = format(
    nextOccurrenceDateAfter(
      sourceDate,
      sourceDate,
      recurrence.recurrenceType,
      recurrence.repeatInterval
    ),
    "yyyy-MM-dd"
  );

  const occurrenceOptions = Array.from(
    { length: Math.max(maximumOccurrenceCount - 1, 0) },
    (_, index) => {
      const count = index + 2;
      return { label: t`${count} instances`, value: String(count) };
    }
  );

  // Whether ANY cadence -- the current one, or a fallback -- has room for at least
  // one future occurrence before maximumEndDate. If not, there's nothing valid to
  // enable: falling back to "daily" and forcing occurrenceCount to 2 anyway would
  // let the patron submit a source whose upsert cannot form a recurring series.
  const canEnableRecurrence =
    maximumOccurrenceCount >= 2 || availableCadenceOptions.length > 0;

  const updateRecurrence = (
    nextRecurrence: NonNullable<typeof bookingWizardData.recurrence>
  ) => {
    setBookingWizardData({
      ...bookingWizardData,
      recurrence: nextRecurrence,
    });
  };

  return (
    <VStack p={5} gap={4} bg="surface-primary" borderRadius="4">
      <VStack gap={1}>
        <Text variant="mainBaseSemiBold">{t`Recurrence`}</Text>
        <Text variant="mainSmallMedium" color="text-secondary">
          {t`This request will repeat based on your settings.`}
        </Text>
      </VStack>

      <HStack alignItems="center" justifyContent="space-between">
        <Text variant="mainSmallSemiBold">{t`Make recurring`}</Text>
        <ToggleSwitch
          isChecked={recurrence.enabled}
          isDisabled={!canEnableRecurrence}
          showToggleLabel
          accessibilityLabel={t`Make request recurring`}
          testID="make-request-recurring-toggle"
          onToggle={(enabled) => {
            if (!enabled) {
              updateRecurrence({ ...recurrence, enabled });
              return;
            }

            if (!canEnableRecurrence) {
              return;
            }

            const currentCadenceHasFutureInstance = maximumOccurrenceCount >= 2;
            const nextCadence = currentCadenceHasFutureInstance
              ? {
                  recurrenceType: recurrence.recurrenceType,
                  repeatInterval: recurrence.repeatInterval,
                }
              : getRecurrenceForCadence(
                  availableCadenceOptions[0]?.value ?? "daily"
                );
            const nextMaximumCount = getMaximumOccurrenceCount({
              maximumEndDate,
              ...nextCadence,
              sourceDate,
            });
            const endDate = recurrenceEndDate(sourceDate, {
              ...recurrence,
              ...nextCadence,
            });

            updateRecurrence({
              ...recurrence,
              ...nextCadence,
              enabled,
              endDate,
              occurrenceCount: Math.max(
                2,
                Math.min(recurrence.occurrenceCount ?? 4, nextMaximumCount)
              ),
            });
          }}
        />
      </HStack>

      {recurrence.enabled ? (
        <>
          <VStack gap={2}>
            <Text variant="mainSmallSemiBold">{t`Repeats`}</Text>
            <DropdownSelect
              value={cadenceValue}
              options={availableCadenceOptions}
              testID="request-recurrence-cadence-select"
              onChange={(value) => {
                const nextRecurrence = getRecurrenceForCadence(value);
                const nextMaximumCount = getMaximumOccurrenceCount({
                  maximumEndDate,
                  ...nextRecurrence,
                  sourceDate,
                });
                const occurrenceCount = Math.min(
                  recurrence.occurrenceCount ?? 4,
                  nextMaximumCount
                );
                const endDate = recurrenceEndDate(sourceDate, {
                  ...recurrence,
                  ...nextRecurrence,
                });

                updateRecurrence({
                  ...recurrence,
                  ...nextRecurrence,
                  endDate,
                  occurrenceCount,
                });
              }}
            />
          </VStack>

          <VStack gap={2}>
            <Text variant="mainSmallSemiBold">{t`Ends`}</Text>
            <RadioGroup
              value={recurrence.endCondition}
              onChange={(value) => {
                updateRecurrence({
                  ...recurrence,
                  endCondition:
                    value === "ON_DATE" ? "ON_DATE" : "AFTER_OCCURRENCES",
                  endDate:
                    value === "ON_DATE"
                      ? recurrence.endDate ?? maximumEndDate
                      : recurrence.endDate,
                });
              }}
              accessibilityLabel={t`Recurrence ending`}
              accessible={false}
            >
              <VStack gap={2}>
                <Radio value="ON_DATE" label={t`On a specific date`} />
                {recurrence.endCondition === "ON_DATE" ? (
                  <DatePickerInput
                    selectedDate={selectedEndDate}
                    firstAvailableDate={firstFutureOccurrenceDate}
                    lastAvailableDate={maximumEndDate}
                    clubWeekStartsOn={clubWeekStartsOn}
                    returnValueFormat="yyyy-MM-dd"
                    displayValueFormat="MMM do, yyyy"
                    accessibilityLabel={t`Recurring request end date`}
                    testID="request-recurrence-end-date"
                    onChange={(endDate) => {
                      updateRecurrence({ ...recurrence, endDate });
                    }}
                  />
                ) : null}
              </VStack>
              <VStack gap={2}>
                <Radio value="AFTER_OCCURRENCES" label={t`After`} />
                {recurrence.endCondition === "AFTER_OCCURRENCES" ? (
                  <DropdownSelect
                    value={String(
                      Math.min(
                        recurrence.occurrenceCount ?? 4,
                        maximumOccurrenceCount
                      )
                    )}
                    options={occurrenceOptions}
                    testID="request-recurrence-occurrence-count-select"
                    onChange={(value) => {
                      const occurrenceCount = Number(value);
                      if (Number.isInteger(occurrenceCount)) {
                        updateRecurrence({ ...recurrence, occurrenceCount });
                      }
                    }}
                  />
                ) : null}
              </VStack>
            </RadioGroup>
          </VStack>
        </>
      ) : null}
    </VStack>
  );
};
